<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Pagination Component
      p.text--secondary A navigation component for splitting large datasets across multiple pages with customizable display options.

    // Pagination Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in paginationVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped variations (side by side)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
              .d-flex.flex-column.gap-4
                div(v-for="pagination in variation.paginations" :key="pagination.label" class="d-flex flex-column align-start mb-4")
                  small.text--secondary.mb-2(v-if="pagination.hint") {{ pagination.hint }}
                  yz-pagination(v-model="pagination.currentPage" v-bind="pagination.props")
                  small.text--secondary.mt-1 Current page: {{ pagination.currentPage }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                div(v-for="pagination in variation.paginations" :key="pagination.label" class="mb-3")
                  .d-flex.align-center.mb-1
                    yz-btn(icon-only size="md" :icon="copiedCode === pagination.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === pagination.code ? 'success' : ''" @click="copyCode(pagination.code)")
                    span.font-weight-medium.ml-2 {{ pagination.label }}
                  pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                    code {{ pagination.code }}

          // Individual variations
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
              yz-pagination(v-model="variation.currentPage" v-bind="variation.props")
              small.text--secondary.mt-2 Current page: {{ variation.currentPage }}

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in paginationProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      paginationVariations: [
        {
          name: 'Basic Pagination',
          id: 'basic-pagination',
          description: 'Default pagination with page navigation',
          currentPage: 1,
          props: {
            pageSize: 10,
            count: 100
          },
          code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="100")'
        },
        {
          name: 'Pagination Types',
          id: 'pagination-types',
          description: 'Different display types for pagination information',
          isGroup: true,
          paginations: [
            {
              label: 'Page Type',
              currentPage: 1,
              props: {
                pageSize: 10,
                count: 100,
                type: 'page',
                text: 'items'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="100" type="page" text="items")',
              hint: 'Shows current page of total pages'
            },
            {
              label: 'Item Type',
              currentPage: 1,
              props: {
                pageSize: 10,
                count: 100,
                type: 'item',
                text: 'records'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="100" type="item" text="records")',
              hint: 'Shows item range of total items'
            },
            {
              label: 'No Type Display',
              currentPage: 1,
              props: {
                pageSize: 10,
                count: 100
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="100")',
              hint: 'No text display, navigation only'
            }
          ]
        },
        {
          name: 'Navigation Styles',
          id: 'navigation-styles',
          description: 'Different navigation button styles',
          isGroup: true,
          paginations: [
            {
              label: 'Arrow Navigation',
              currentPage: 2,
              props: {
                pageSize: 10,
                count: 100,
                arrow: true,
                type: 'page',
                text: 'items'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="100" arrow type="page" text="items")',
              hint: 'Arrow icons for navigation'
            },
            {
              label: 'Text Navigation',
              currentPage: 2,
              props: {
                pageSize: 10,
                count: 100,
                arrow: false,
                type: 'item',
                text: 'records'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="100" :arrow="false" type="item" text="records")',
              hint: 'Text buttons for navigation'
            }
          ]
        },
        {
          name: 'Page Size Control',
          id: 'page-size-control',
          description: 'Pagination with changeable page size',
          currentPage: 1,
          props: {
            pageSize: 5,
            count: 100,
            changePageSize: true,
            type: 'item',
            text: 'results'
          },
          code: 'yz-pagination(v-model="currentPage" :page-size.sync="pageSize" :count="100" change-page-size type="item" text="results")'
        },
        {
          name: 'Different Page Sizes',
          id: 'different-page-sizes',
          description: 'Various page sizes and their effects',
          isGroup: true,
          paginations: [
            {
              label: 'Small Pages (5)',
              currentPage: 3,
              props: {
                pageSize: 5,
                count: 50,
                type: 'page',
                text: 'items'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="5" :count="50" type="page" text="items")',
              hint: '5 items per page, 10 total pages'
            },
            {
              label: 'Medium Pages (20)',
              currentPage: 2,
              props: {
                pageSize: 20,
                count: 100,
                type: 'item',
                text: 'records'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="20" :count="100" type="item" text="records")',
              hint: '20 items per page, 5 total pages'
            },
            {
              label: 'Large Pages (50)',
              currentPage: 1,
              props: {
                pageSize: 50,
                count: 75,
                arrow: true,
                type: 'page',
                text: 'entries'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="50" :count="75" arrow type="page" text="entries")',
              hint: '50 items per page, 2 total pages'
            }
          ]
        },
        {
          name: 'Edge Cases',
          id: 'edge-cases',
          description: 'Pagination behavior in edge cases',
          isGroup: true,
          paginations: [
            {
              label: 'Single Page',
              currentPage: 1,
              props: {
                pageSize: 10,
                count: 8,
                type: 'item',
                text: 'items'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="8" type="item" text="items")',
              hint: 'Only 8 items, fits in one page'
            },
            {
              label: 'No Items',
              currentPage: 1,
              props: {
                pageSize: 10,
                count: 0,
                type: 'item',
                text: 'results'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="0" type="item" text="results")',
              hint: 'Empty dataset'
            },
            {
              label: 'Large Dataset',
              currentPage: 15,
              props: {
                pageSize: 50,
                count: 2500,
                arrow: true,
                type: 'page',
                text: 'records'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="50" :count="2500" arrow type="page" text="records")',
              hint: '2500 items, 50 total pages'
            }
          ]
        },
        {
          name: 'Complete Examples',
          id: 'complete-examples',
          description: 'Full-featured pagination examples',
          isGroup: true,
          paginations: [
            {
              label: 'Complete with Page Size',
              currentPage: 3,
              props: {
                pageSize: 20,
                count: 237,
                changePageSize: true,
                arrow: true,
                type: 'item',
              },
              code: 'yz-pagination(v-model="currentPage" :page-size.sync="pageSize" :count="237" change-page-size arrow type="item")',
              hint: 'All features enabled'
            }
          ]
        },
        {
          name: 'YC Version (Legacy Style)',
          id: 'yc-version',
          description: 'Legacy pagination style with dropdown page selector',
          isGroup: true,
          paginations: [
            {
              label: 'YC with Page Size Selector',
              currentPage: 2,
              props: {
                pageSize: 10,
                count: 100,
                version: 'yc',
                changePageSize: true,
                ycText: 'Items per page'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size.sync="pageSize" :count="100" version="yc" change-page-size yc-text="Items per page")',
              hint: 'YC version with page size selector and custom label'
            },
            {
              label: 'YC with Default Label',
              currentPage: 3,
              props: {
                pageSize: 20,
                count: 150,
                version: 'yc',
                changePageSize: true
              },
              code: 'yz-pagination(v-model="currentPage" :page-size.sync="pageSize" :count="150" version="yc" change-page-size)',
              hint: 'YC version with default "items per page" label'
            },
            {
              label: 'YC Navigation Only',
              currentPage: 1,
              props: {
                pageSize: 10,
                count: 50,
                version: 'yc'
              },
              code: 'yz-pagination(v-model="currentPage" :page-size="10" :count="50" version="yc")',
              hint: 'YC version without page size selector'
            }
          ]
        }
      ],
      paginationProps: [
        {
          name: 'value (v-model)',
          type: 'Number',
          default: '1',
          description: 'Current page number (1-based)'
        },
        {
          name: 'pageSize',
          type: 'Number',
          default: '10',
          description: 'Number of items per page. Use .sync modifier when changePageSize is enabled'
        },
        {
          name: 'count',
          type: 'Number',
          default: '0',
          description: 'Total number of items in the dataset'
        },
        {
          name: 'type',
          type: 'String',
          default: 'null',
          description: 'Display type: "page" shows page info, "item" shows item range, null shows no text'
        },
        {
          name: 'text',
          type: 'String',
          default: 'null',
          description: 'Label text to display alongside pagination info for YZ version (e.g. "items", "records")'
        },
        {
          name: 'ycText',
          type: 'String',
          default: '"items per page"',
          description: 'Label text for page size selector in YC version. Only shown when changePageSize is true'
        },
        {
          name: 'arrow',
          type: 'Boolean',
          default: 'false',
          description: 'Use arrow icons instead of "Back"/"Next" text for navigation'
        },
        {
          name: 'changePageSize',
          type: 'Boolean',
          default: 'false',
          description: 'Show dropdown to change page size. For YZ version, shows "Show item:" label. For YC version, shows ycText label. Emits update:pageSize event'
        },
        {
          name: 'attach',
          type: 'Boolean',
          default: 'false',
          description: 'Attach page size dropdown to document.body'
        },
        {
          name: 'menuTop',
          type: 'Boolean',
          default: 'false',
          description: 'Position page size dropdown above the button'
        },
        {
          name: 'version',
          type: 'String',
          default: 'yz',
          description: 'UI version: "yz" for modern design with y-button components, "yc" for legacy style with dropdown page selector'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>